import React, { useState } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';

const sectionSx = {
  p: 2,
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(255,255,255,0.03)',
};

const listBoxSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  p: 1.5,
  maxHeight: 140,
  overflowY: 'auto',
  bgcolor: 'background.default',
};

/**
 * Local compliance user settings only.
 * Account creation, roles, and job flags are managed in the Employee Portal.
 */
const UserManagement = ({ db, usersList, fetchUsers }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editAssignedManagers, setEditAssignedManagers] = useState([]);
  const [editTimesheetRequired, setEditTimesheetRequired] = useState(false);
  const [editTravelAllowance, setEditTravelAllowance] = useState('No');
  const [editDefaultLunch, setEditDefaultLunch] = useState('');
  const [editContractedHours, setEditContractedHours] = useState('');
  const [editSalaried, setEditSalaried] = useState('No');
  const [resetPinValue, setResetPinValue] = useState('');
  const [status, setStatus] = useState('');

  const availableManagers = usersList.filter((u) => u.isLineManager === true);
  const pendingPinUsers = usersList.filter((u) => u.changePinRequired);
  const selectedUser = usersList.find((u) => u.initials === selectedUserId);

  const toggleEditManager = (id) => {
    setEditAssignedManagers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectUser = (e) => {
    const userId = e.target.value;
    setSelectedUserId(userId);
    setStatus('');
    setResetPinValue('');
    const user = usersList.find((u) => u.initials === userId);
    if (user) {
      setEditDisplayName(user.displayName || '');
      setEditTimesheetRequired(Boolean(user.timesheetRequired));
      setEditTravelAllowance(user.travelAllowance || 'No');
      setEditDefaultLunch(user.defaultLunch || '');
      setEditContractedHours(user.contractedHours || '');
      setEditSalaried(user.salaried || 'No');
      const mgrs = user.assignedManager;
      if (Array.isArray(mgrs)) setEditAssignedManagers(mgrs);
      else if (mgrs) setEditAssignedManagers([mgrs]);
      else setEditAssignedManagers([]);
    } else {
      setEditDisplayName('');
      setEditAssignedManagers([]);
      setEditTimesheetRequired(false);
      setEditTravelAllowance('No');
      setEditDefaultLunch('');
      setEditContractedHours('');
      setEditSalaried('No');
    }
  };

  const handleUpdateName = async () => {
    if (!selectedUserId) return;
    await updateDoc(doc(db, 'users', selectedUserId), { displayName: editDisplayName });
    fetchUsers();
    setStatus('Display name updated.');
  };

  const handleUpdateSettings = async () => {
    if (!selectedUserId) return;
    await updateDoc(doc(db, 'users', selectedUserId), {
      assignedManager: editAssignedManagers,
      timesheetRequired: editTimesheetRequired,
      travelAllowance: editTravelAllowance,
      defaultLunch: editDefaultLunch,
      contractedHours: editContractedHours,
      salaried: editSalaried,
    });
    fetchUsers();
    setStatus('Timesheet and manager settings saved.');
  };

  const handleResetPin = async () => {
    if (!selectedUserId || !resetPinValue.trim()) return;
    await updateDoc(doc(db, 'users', selectedUserId), {
      pin: resetPinValue.trim(),
      changePinRequired: true,
    });
    fetchUsers();
    setStatus('PIN reset — user must change it on next login.');
    setResetPinValue('');
  };

  const handleDeleteUser = async () => {
    if (!selectedUserId) return;
    if (!window.confirm(`Delete local profile ${selectedUserId}? This does not remove their Employee Portal account.`)) {
      return;
    }
    await deleteDoc(doc(db, 'users', selectedUserId));
    setSelectedUserId('');
    fetchUsers();
    setStatus('Local user profile deleted.');
  };

  return (
    <Box
      sx={{
        maxWidth: 960,
        mx: 'auto',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' },
        gap: 2.5,
        textAlign: 'left',
      }}
    >
      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Manage users
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Accounts, roles, and job flags are managed in the Employee Portal. Use this screen for
          local display name, timesheet settings, managers, and PIN reset.
        </Typography>

        <TextField
          select
          label="Select user"
          value={selectedUserId}
          onChange={handleSelectUser}
          sx={{ mb: 2.5 }}
        >
          <MenuItem value="">— Select user —</MenuItem>
          {usersList
            .slice()
            .sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')))
            .map((u) => (
              <MenuItem key={u.initials} value={u.initials}>
                {u.displayName} ({u.initials}){u.role ? ` · ${u.role}` : ''}
              </MenuItem>
            ))}
        </TextField>

        {selectedUserId && (
          <Stack spacing={2}>
            {status && (
              <Alert severity="success" onClose={() => setStatus('')}>
                {status}
              </Alert>
            )}

            <Box sx={sectionSx}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5, letterSpacing: '0.12em' }}
              >
                Basic info
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  label="Display name"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Button variant="contained" onClick={handleUpdateName} sx={{ alignSelf: 'stretch' }}>
                  Update name
                </Button>
              </Stack>
              {selectedUser && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
                  Initials {selectedUser.initials}
                  {selectedUser.role ? ` · Role ${selectedUser.role} (from Employee Portal)` : ''}
                </Typography>
              )}
            </Box>

            <Box sx={sectionSx}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: editTimesheetRequired ? 1.5 : 0 }}
              >
                <Typography
                  variant="overline"
                  color="primary.main"
                  sx={{ letterSpacing: '0.12em' }}
                >
                  Timesheet settings
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editTimesheetRequired}
                      onChange={(e) => setEditTimesheetRequired(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Enable timesheet"
                />
              </Stack>

              {editTimesheetRequired && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    select
                    label="Fuel allowance"
                    value={editTravelAllowance}
                    onChange={(e) => setEditTravelAllowance(e.target.value)}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                  <TextField
                    select
                    label="Salaried"
                    value={editSalaried}
                    onChange={(e) => setEditSalaried(e.target.value)}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                  <TextField
                    type="number"
                    label="Lunch (mins)"
                    value={editDefaultLunch}
                    onChange={(e) => setEditDefaultLunch(e.target.value)}
                  />
                  <TextField
                    type="number"
                    label="Hours / day"
                    value={editContractedHours}
                    onChange={(e) => setEditContractedHours(e.target.value)}
                  />
                </Box>
              )}
            </Box>

            <Box sx={sectionSx}>
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ display: 'block', mb: 1.5, letterSpacing: '0.12em' }}
              >
                Assigned line managers
              </Typography>
              <Box sx={listBoxSx}>
                {availableManagers.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No line managers found.
                  </Typography>
                ) : (
                  availableManagers.map((m) => (
                    <FormControlLabel
                      key={m.initials}
                      control={
                        <Checkbox
                          size="small"
                          checked={editAssignedManagers.includes(m.initials)}
                          onChange={() => toggleEditManager(m.initials)}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          {m.displayName} ({m.initials})
                        </Typography>
                      }
                      sx={{ display: 'flex', m: 0, mb: 0.5 }}
                    />
                  ))
                )}
              </Box>
            </Box>

            <Button variant="contained" onClick={handleUpdateSettings} fullWidth>
              Save settings
            </Button>

            <Divider />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Reset user PIN
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  label="New temporary PIN"
                  value={resetPinValue}
                  onChange={(e) => setResetPinValue(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" color="warning" onClick={handleResetPin}>
                  Reset PIN
                </Button>
              </Stack>
              <Button variant="outlined" color="error" fullWidth onClick={handleDeleteUser}>
                Delete local profile
              </Button>
            </Box>
          </Stack>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, alignSelf: 'start' }}>
        <Typography variant="h6" color="error.main" sx={{ mb: 1.5 }}>
          Pending PIN changes
        </Typography>
        {pendingPinUsers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            All users have updated their PINs.
          </Typography>
        ) : (
          <Stack spacing={1.25} component="ul" sx={{ m: 0, pl: 2.5 }}>
            {pendingPinUsers.map((u) => (
              <Box component="li" key={u.initials}>
                <Typography variant="body2">
                  <strong>{u.displayName}</strong> ({u.initials})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Temp PIN: {u.pin}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Box>
  );
};

export default UserManagement;
